import React, { useState, useEffect } from "react";
import { usePageTitle } from "../../hooks/usePageTitle";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Skeleton } from "../../components/ui/skeleton";
import {
  Target,
  Mail,
  Package,
  TrendingUp,
  AlertCircle,
  Clock,
} from "lucide-react";
import { ppcFarmService } from "../../services/ppcFarmService";
import { formatDate } from "../../utils/dateUtils";
import PpcFarmAnalyticsDashboard from "../../components/ppc-farm/PpcFarmAnalyticsDashboard";

const PpcFarmDashboardPage: React.FC = () => {
  usePageTitle("PPC Farm Mail Tracker");


  // Dashboard metrics state
  const [dashboardMetrics, setDashboardMetrics] = useState<{
    totalCohorts: number;
    mailersSent: number;
    pendingShipments: number;
    overallConversionRate: number;
    lastSync: string | null;
  } | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [metricsError, setMetricsError] = useState<string | null>(null);


  // Fetch dashboard metrics on component mount
  useEffect(() => {
    const fetchDashboardMetrics = async () => {
      try {
        setMetricsLoading(true);
        setMetricsError(null);
        const metrics = await ppcFarmService.getDashboardMetrics();
        setDashboardMetrics(metrics);
      } catch (error) {
        console.error("Error fetching dashboard metrics:", error);
        setMetricsError("Failed to load dashboard metrics");
      } finally {
        setMetricsLoading(false);
      }
    };

    fetchDashboardMetrics();
  }, []);


  // Metrics cards configuration
  const metricsCards = [
    {
      title: "Total Cohorts",
      value: dashboardMetrics?.totalCohorts?.toLocaleString() || "0",
      icon: Target,
      color: "text-green-600 dark:text-green-400",
      bgColor: "bg-green-100 dark:bg-green-900/20",
    },
    {
      title: "Mailers Sent",
      value: dashboardMetrics?.mailersSent?.toLocaleString() || "0",
      icon: Mail,
      color: "text-purple-600 dark:text-purple-400",
      bgColor: "bg-purple-100 dark:bg-purple-900/20",
    },
    {
      title: "Pending Shipments",
      value: dashboardMetrics?.pendingShipments?.toLocaleString() || "0",
      icon: Package,
      color: "text-orange-600 dark:text-orange-400",
      bgColor: "bg-orange-100 dark:bg-orange-900/20",
    },
    {
      title: "Overall Conversion Rate",
      value: dashboardMetrics?.overallConversionRate
        ? `${dashboardMetrics.overallConversionRate.toFixed(2)}%`
        : "0.00%",
      icon: TrendingUp,
      color: "text-indigo-600 dark:text-indigo-400",
      bgColor: "bg-indigo-100 dark:bg-indigo-900/20",
    },
    {
      title: "Last Sync",
      value: dashboardMetrics?.lastSync
        ? formatDate(dashboardMetrics.lastSync)
        : "Never",
      icon: Clock,
      color: "text-gray-600 dark:text-gray-400",
      bgColor: "bg-gray-100 dark:bg-gray-900/20",
    },
  ];


  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2">
          PPC Farm Overview
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Welcome to the PPC Farm Direct Mail Tool dashboard
        </p>
      </div>


      {/* Key Metrics Dashboard */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
          Key Metrics
        </h2>

        {metricsError ? (
          <Card className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20">
            <CardContent className="p-6">
              <div className="flex items-center space-x-2 text-red-600 dark:text-red-400">
                <AlertCircle className="h-5 w-5" />
                <span>Error loading metrics: {metricsError}</span>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {metricsCards.map((metric, index) => {
              const IconComponent = metric.icon;
              return (
                <Card key={index} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-3 flex items-center justify-center">
                    <div className="text-center">
                      <div className="flex items-center justify-center mb-1">
                        <IconComponent className={`h-4 w-4 ${metric.color} mr-2`} />
                        <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                          {metric.title}
                        </p>
                      </div>
                      {metricsLoading ? (
                        <Skeleton className="h-6 w-16" />
                      ) : (
                        <p className="text-xl font-bold text-gray-900 dark:text-white">
                          {metric.value}
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>


      {/* New Analytics Dashboard */}
      <PpcFarmAnalyticsDashboard className="mb-8" />
    </div>
  );
};

export default PpcFarmDashboardPage;
